/**
 * Word renderer — converts ParsedNode[] into formatted Word paragraphs.
 *
 * Uses Office JS Word.run() APIs with run-level font control.
 * Each InlineSegment becomes a separate text range with its own font settings.
 *
 * Mirrors word_writer.py _add_rich_paragraph() (line 255) and
 * _add_table() (line 343) but using Office JS instead of python-docx.
 */

/* global Word */

import type { FormatterConfig, HeadingConfig } from "./formatterConfig";
// ``parseInlineSegments`` already handles ``**bold**``, ``*italic*``, links,
// and inline markers. We reuse it for table cells so cell text rendering
// stays consistent with paragraph rendering — see ``renderTable`` below.
import { parseCellImage, parseInlineSegments } from "./markdownParser";
import type { InlineSegment, ParsedNode, StructuredTableData } from "./types";

// Clears the highlight on a freshly-inserted run so the paragraph mark it
// touches stops inheriting a styled run's ``font.highlightColor`` (Pink for
// ``[MISSING INFO]``, Yellow for ``[REVIEWER]``, etc.) and bleeding it into
// the next ``insertText`` / ``insertParagraph`` within the same section's
// ``Word.run``. ``"NoColor"`` is the documented Word.HighlightColor "off"
// value; the assignment is centralised so swapping it (e.g. for ``null``)
// stays a one-line change if a future Word build rejects the literal.
function clearHighlight(range: Word.Range): void {
  // Office.js's documented removal value for a highlight is ``null`` — NOT
  // the string literals "NoColor"/"None", which Word for Web rejects with
  // RichApi.InvalidArgument at sync time (torching the whole batch). This
  // used to be a no-op on the assumption that fresh insertText runs default
  // to un-highlighted; on Word for Web they instead inherit the previous
  // run's highlight via the paragraph-mark chain, so every [REGULATOR FLAG:…]
  // / [MISSING INFO:…] marker bled its highlight into the following text.
  // The typed surface declares ``highlightColor: string``, hence the cast.
  range.font.highlightColor = null as unknown as string;
}

// ---------------------------------------------------------------------------
// Node renderer type — one function per node type (Open/Closed)
// ---------------------------------------------------------------------------

type NodeRenderer = (insertAfter: Word.Paragraph, node: ParsedNode, config: FormatterConfig) => Word.Paragraph;

// ---------------------------------------------------------------------------
// Renderer registry
// ---------------------------------------------------------------------------

const renderers: Record<string, NodeRenderer> = {
  heading: renderHeading,
  paragraph: renderParagraph,
  blockquote: renderBlockquote,
  list_item: renderListItem,
  table: renderTable,
  structured_table: renderStructuredTable,
  image: renderImage,
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render an array of parsed nodes into the Word document.
 *
 * Inserts formatted paragraphs after `insertAfter`, returns the last
 * paragraph inserted (for chaining).
 *
 * # Why this is async + syncs between every node
 *
 * Each renderer calls `cursor.insertParagraph(...)` (or insertTable, …)
 * and returns the resulting Word.Paragraph proxy. The next iteration
 * uses that proxy as the `insertAfter` anchor for the *next* paragraph.
 *
 * Office.js's tolerance for chaining inserts on a proxy whose paragraph
 * hasn't yet been materialised by a sync is uneven: the first insert
 * resolves (anchor is the real scaffold heading), but follow-on inserts
 * whose anchor is an unsynced proxy get silently dropped — the
 * downstream user sees only the first node (or sometimes the first two)
 * land in the live doc. Tables vanish for the same reason.
 *
 * Syncing after every node forces each anchor proxy to resolve to a
 * real paragraph location before the next renderer queues its insert.
 * It costs one round-trip per node but is the only way to make
 * the proxy chain reliable.
 *
 * @param context - The Word request context (caller's Word.run scope)
 * @param insertAfter - The heading paragraph to insert content after
 * @param nodes - Parsed markdown nodes from markdownParser
 * @param config - Formatting configuration
 * @returns The last paragraph inserted
 */
export async function renderNodes(
  context: Word.RequestContext,
  insertAfter: Word.Paragraph,
  nodes: ParsedNode[],
  config: FormatterConfig,
): Promise<Word.Paragraph> {
  let cursor = insertAfter;
  // ``lastGood`` is the most recent paragraph proxy that was created
  // AND survived a successful ``context.sync()``. After a failed sync
  // the freshly-returned ``cursor`` proxy is invalidated by Word.js
  // (subsequent uses throw ``"The object path '_reference().insertParagraph()'
  // isn't working for what you're trying to do"``), so we fall back to
  // ``lastGood`` for the next iteration's anchor. The initial scaffold
  // heading is, by definition, a real synced paragraph.
  let lastGood: Word.Paragraph = insertAfter;

  for (let idx = 0; idx < nodes.length; idx++) {
    const node = nodes[idx];
    const renderer = renderers[node.type];
    if (!renderer) continue;
    try {
      cursor = renderer(cursor, node, config);
      // Materialise the cursor before the next renderer's anchor uses
      // it. See doc comment above; this is load-bearing for the live
      // doc render.
      await context.sync();
      // Sync succeeded — promote cursor to the last-known-good anchor.
      lastGood = cursor;
    } catch (err) {
      // One bad node must not torch the rest of the section.
      //
      // Office.js rejects some property assignments at sync time with
      // a generic ``RichApi.Error: The argument that you provided is
      // not valid``; before this catch existed, the throw propagated
      // to the outer ``insertSectionContent`` catch and the queue
      // discarded every node still to come, leaving the doc with
      // only the first paragraph. Now: skip the offending node,
      // log enough detail to fix it, keep rendering.
      const oe = err as { debugInfo?: unknown; message?: string };
      console.warn(`[formatter] node #${idx} (type=${node.type}) failed to render; skipping.`, {
        err,
        debugInfo: oe?.debugInfo,
        node,
      });
      // Reset cursor to the last known-good paragraph. The renderer
      // that just threw may have left the ``cursor`` proxy pointing at
      // an unmaterialised paragraph reference that Word.js will reject
      // on every subsequent insert ("object path '_reference()
      // .insertParagraph()' isn't working"). Anchoring after the last
      // successfully-synced paragraph keeps insertion order roughly
      // correct (left-to-right, just missing the dropped node) and
      // avoids the cascade where every remaining node fails on a dead
      // proxy.
      cursor = lastGood;
    }
    // "blank" nodes are skipped — no empty paragraphs
  }

  return cursor;
}

// ---------------------------------------------------------------------------
// Individual renderers
// ---------------------------------------------------------------------------

/** Font size for an LLM-emitted sub-heading at a given markdown level.
 *  Deliberately NOT mapped to Word's built-in HeadingN styles — see
 *  ``renderHeading`` for the reason. Pure lookup; the parser caps the
 *  level to 1..6 so the fallback only fires defensively. */
function llmHeadingSize(level: number, sizeBody: number): number {
  switch (level) {
    case 1:
    case 2:
      return 13;
    case 3:
      return 12;
    case 4:
      return 11;
    case 5:
    case 6:
      return sizeBody;
    default:
      return sizeBody;
  }
}

function renderHeading(insertAfter: Word.Paragraph, node: ParsedNode, config: FormatterConfig): Word.Paragraph {
  const level = node.headingLevel ?? 2;
  const text = node.headingText ?? "";
  const headingConfig: HeadingConfig | undefined = config.headings[level];

  // # DECISION (2026-05-15): render LLM-emitted sub-headings as bold
  // #   paragraphs under the ``Normal`` style — DO NOT use Word's
  // #   built-in ``Heading2``/``Heading3``/… styles.
  // # WHY: the CTD scaffold (``scaffoldDocument`` in wordWriter.ts)
  // #   owns the numbered outline — every skeleton row writes a
  // #   ``HeadingN`` paragraph that Word auto-numbers (3.2.P, 3.2.P.1,
  // #   3.2.P.2.1.2 …). When the agent then emits ``## Composition`` or
  // #   similar, the previous renderer ALSO applied a HeadingN style,
  // #   so Word inserted an outline number into the agent's sub-heading
  // #   that collided with the skeleton and pushed the doc structure
  // #   out of shape. Keeping agent content on ``Normal`` styled,
  // #   bold-bodied paragraphs preserves the skeleton's numbering
  // #   exclusively for the scaffold rows.
  // # DATE: 2026-05-15
  const para = insertAfter.insertParagraph(text, "After");
  para.styleBuiltIn = "Normal";
  para.spaceBefore = 6;
  para.spaceAfter = 3;

  const range = para.getRange("Whole");
  range.font.name = config.font.name;
  range.font.size = headingConfig?.fontSize ?? llmHeadingSize(level, config.font.sizeBody);
  range.font.bold = headingConfig?.bold ?? true;
  range.font.color = headingConfig?.color ?? "#000000";
  clearHighlight(range);

  return para;
}

function renderParagraph(insertAfter: Word.Paragraph, node: ParsedNode, config: FormatterConfig): Word.Paragraph {
  const segments = node.segments ?? [];
  if (segments.length === 0) return insertAfter;

  // Check if the entire paragraph is a highlighted marker
  // (single segment that is not_found, missing_info, cannot_resolve, or reviewer)
  if (
    segments.length === 1 &&
    (segments[0].type === "not_found" ||
      segments[0].type === "missing_info" ||
      segments[0].type === "cannot_resolve" ||
      segments[0].type === "missing_table" ||
      segments[0].type === "reviewer" ||
      segments[0].type === "regulator_flag" ||
      segments[0].type === "language_flag")
  ) {
    return renderHighlightedParagraph(insertAfter, segments[0], config);
  }

  const para = insertAfter.insertParagraph("", "After");
  para.styleBuiltIn = "Normal";
  // # DECISION (2026-05-15): only set ``spaceAfter`` here. Word for Web
  // #   rejects the other property assignments that used to live in
  // #   this block:
  // #     - ``(para as any).alignment``: ``any`` cast is the tell —
  // #       the property name is rejected as "argument not valid".
  // #     - ``para.lineUnitAfter = 0``: rejected by some Web builds.
  // #     - ``para.lineSpacing = 13.8``: non-integer points; rejected.
  // #   When the batch's sync fires, ANY of those rejected properties
  // #   torches the whole batch — text fills, font formatting,
  // #   everything in the same Word.run sub-batch is rolled back. The
  // #   user only sees the empty heading from before this paragraph.
  // # WHY just spaceAfter: paragraphs need *some* gap between them so
  // #   prose doesn't run together. ``spaceAfter`` is on the typed
  // #   surface in every Word.js build and accepts plain numbers.
  // # FOR JUSTIFIED ALIGNMENT (if reintroduced later): adjust the
  // #   ``Normal`` style globally at scaffold time, not per paragraph.
  // # DATE: 2026-05-15
  para.spaceAfter = config.paragraphSpaceAfter;

  // Insert each segment as a separate text range with run-level formatting
  for (const segment of segments) {
    renderSegment(para, segment, config);
  }

  return para;
}

function renderBlockquote(insertAfter: Word.Paragraph, node: ParsedNode, config: FormatterConfig): Word.Paragraph {
  const segments = node.segments ?? [];
  if (segments.length === 0) return insertAfter;

  const bq = config.blockquote;
  const para = insertAfter.insertParagraph("", "After");
  para.styleBuiltIn = "Normal";
  para.spaceAfter = 1;
  // Drop: ``lineSpacing = 13.8`` — Word for Web rejects non-integer
  // point values on this property and the rejection torches the whole
  // batch. See renderParagraph for the full rationale.
  para.leftIndent = bq.indentBase + (node.indentLevel ?? 0) * bq.indentPerLevel;

  for (const segment of segments) {
    const range = para.insertText(segment.text, "End");
    range.font.name = config.font.name;
    range.font.size = config.font.sizeBody;
    range.font.color = bq.color;
  }

  return para;
}

function renderListItem(insertAfter: Word.Paragraph, node: ParsedNode, config: FormatterConfig): Word.Paragraph {
  const segments = node.segments ?? [];
  if (segments.length === 0) return insertAfter;

  const isBullet = node.listStyle === "bullet";
  const indent = node.indentLevel ?? 0;

  const ls = config.list;
  const para = insertAfter.insertParagraph("", "After");
  para.styleBuiltIn = "Normal";
  para.spaceAfter = ls.spaceAfter;
  // Drop: ``lineSpacing = 13.8`` (Web rejects non-integer points).
  para.leftIndent = ls.indentBase + indent * ls.indentPerLevel;
  para.firstLineIndent = ls.hangingIndent;

  // Insert bullet prefix for unordered lists
  // (ordered list items already include "1." etc. from the parser)
  if (isBullet) {
    const prefixRange = para.insertText(`${ls.bulletChar}  `, "End");
    // Reset only the properties whose values Word for Web accepts.
    // ``font.underline = "None"`` and ``font.highlightColor = "NoColor"``
    // are rejected at sync as "argument not valid" — they used to live
    // here as defensive hard-resets to prevent missing_info bleed-through,
    // but the chosen string values aren't on the typed enum surface in the
    // Word Web SDK and rejecting them torches the entire batch (bullet +
    // text + every queued op in the same Word.run). The pink-bleed concern
    // is now mitigated by ``renderSegment`` setting the per-run color
    // explicitly for styled segments; plain-text runs inherit a clean
    // default after the fresh ``insertText`` call above.
    prefixRange.font.name = config.font.name;
    prefixRange.font.size = config.font.sizeBody;
    prefixRange.font.color = config.font.color ?? "#000000";
    prefixRange.font.bold = false;
    prefixRange.font.italic = false;
    prefixRange.font.subscript = false;
    prefixRange.font.superscript = false;
  }

  // Insert content segments
  for (const segment of segments) {
    renderSegment(para, segment, config);
  }

  return para;
}

function renderTable(insertAfter: Word.Paragraph, node: ParsedNode, config: FormatterConfig): Word.Paragraph {
  const rows = node.tableRows ?? [];
  if (rows.length === 0) return insertAfter;

  const numRows = rows.length;
  const numCols = Math.max(...rows.map((r) => r.length));

  // Office JS table insertion
  const table = insertAfter.insertTable(numRows, numCols, "After");

  for (let r = 0; r < numRows; r++) {
    for (let c = 0; c < Math.min(rows[r].length, numCols); c++) {
      const cell = table.getCell(r, c);
      const cellPara = cell.body.paragraphs.getFirst();

      // Clear the cell's existing content (default placeholder paragraph)
      // and split the cell text into inline segments so ``**bold**`` /
      // ``*italic*`` markdown markers render as actual bold / italic
      // runs instead of literal asterisks.
      //
      // # DECISION (2026-05-15): use ``parseInlineSegments`` for cell text.
      // # WHY: cells were previously written with a single
      // #   ``insertText(rows[r][c], "Replace")`` call which committed the
      // #   raw markdown string verbatim. Ingredient cells emitted by the
      // #   agent like ``**Tofacitinib citrate**`` then showed up in Word
      // #   as the literal text "**Tofacitinib citrate**" — asterisks and
      // #   all — instead of bolded "Tofacitinib citrate". Splitting into
      // #   inline segments + per-segment font properties keeps cell
      // #   rendering consistent with paragraph rendering.
      // # SCOPE: the only formatting we apply per segment is ``bold`` and
      // #   ``italic``. We deliberately do NOT touch ``font.highlightColor``
      // #   or ``font.underline`` here — those are the property values
      // #   Word for Web rejects, and keeping them off this code path
      // #   preserves the "filled cells" fix from earlier today. Markers
      // #   like ``[MISSING INFO: …]`` inside cells therefore render as
      // #   plain cell text.
      // # EXCEPTION (2026-09-21): ``regulator_flag`` segments DO get the full
      // #   prose styling (blue italic text + Turquoise highlight) via
      // #   ``applyRegulatorFlagStyle`` — regulatory flags must read the same
      // #   blue whether they land in prose or a table cell (user request). If
      // #   Word for Web rejects ``highlightColor`` inside a cell, that is the
      // #   one segment type affected; the blue text still stands out.
      cellPara.insertText("", "Replace");

      // Image inside a table cell. The pipe row was consumed as a `table`
      // node before the block-image branch could run, so an `![alt](url)` /
      // `[IMAGE DATA: …]` cell would otherwise land as literal markdown text.
      // Drop the same `[Image: …]` placeholder the block path uses and queue
      // the fetch: the image second pass (`formatAndInsert` → `_insertSingleImage`)
      // re-locates the placeholder by scanning `body.paragraphs` — which
      // includes table-cell paragraphs — and replaces it in-place, embedding
      // the picture inside the cell. Skipped on the header row (r === 0), where
      // an image marker is never a real figure.
      const cellImg = r === 0 ? null : parseCellImage(rows[r][c]);
      if (cellImg) {
        const placeholder = cellPara.insertText(`[Image: ${cellImg.alt}]`, "End");
        placeholder.font.name = config.font.name;
        placeholder.font.size = config.font.sizeTable;
        placeholder.font.italic = true;
        placeholder.font.color = config.image.captionColor;
        if (cellImg.url) {
          pendingImages.push({ paragraph: cellPara, url: cellImg.url, alt: cellImg.alt, inCell: true });
        }
        continue;
      }

      const cellSegments = parseInlineSegments(rows[r][c]);
      const isHeaderRow = r === 0 && config.table.headerBold;

      for (const seg of cellSegments) {
        const segRange = cellPara.insertText(seg.text, "End");
        segRange.font.name = config.font.name;
        segRange.font.size = config.font.sizeTable;
        segRange.font.color = "#000000";
        segRange.font.bold = isHeaderRow || seg.type === "bold" || seg.bold === true;
        segRange.font.italic = seg.type === "italic";
        segRange.font.subscript = seg.type === "subscript";
        segRange.font.superscript = seg.type === "superscript";
        // Regulatory flags read the same blue in a cell as in prose (see the
        // EXCEPTION note above). Applied last so it overrides the neutral
        // baseline set on this run.
        if (seg.type === "regulator_flag") {
          applyRegulatorFlagStyle(segRange, config);
        }
      }
    }

    // Header row shading
    if (r === 0 && config.table.headerFill) {
      for (let c = 0; c < numCols; c++) {
        table.getCell(r, c).shadingColor = config.table.headerFill;
      }
    }
  }

  return table.insertParagraph("", "After");
}

// ---------------------------------------------------------------------------
// Segment-level rendering (run-level font control)
// ---------------------------------------------------------------------------

// Office.js gotcha: font.highlightColor (and bold/italic/underline) latch onto
// the paragraph mark and bleed into subsequent insertText calls — including the
// next paragraph and the next table cell. There is no tri-state "inherit"; the
// only safe pattern is "every run sets every property it cares about, every
// time". If you add a new highlighted segment type, add the same hard-reset in
// renderSegment, renderListItem's bullet prefix, and renderTable's cell text.

// [REGULATOR FLAG: …] regulatory-audit recommendation styling — blue italic
// text + Turquoise highlight, distinct from gap red and reviewer gray. Extracted
// so the inline prose path (renderSegment), the whole-paragraph path
// (renderHighlightedParagraph), and the table-cell path (renderTable) all apply
// the identical look and never drift.
function applyRegulatorFlagStyle(range: Word.Range, config: FormatterConfig): void {
  range.font.italic = true;
  range.font.size = config.font.sizeHighlighted;
  range.font.color = "#1565C0";
  range.font.highlightColor = "Turquoise";
}

function renderSegment(paragraph: Word.Paragraph, segment: InlineSegment, config: FormatterConfig): void {
  const range = paragraph.insertText(segment.text, "End");

  // Set the run's neutral baseline. We do NOT touch ``font.underline``
  // here — the value ``"None"`` that was previously assigned is rejected
  // by Word for Web ("argument not valid") and the rejection torches the
  // entire enclosing Word.run batch (the text we just inserted, all
  // subsequent inserts, every queued operation rolls back). That was
  // the root cause of "only the first paragraph renders" + "tables
  // are empty" + "list items missing" across multiple runs.
  //
  // Highlight IS hard-reset via ``clearHighlight`` (assigns ``null`` — the
  // documented removal value, unlike the rejected string "NoColor"): on
  // Word for Web a fresh ``insertText`` run inherits the previous run's
  // highlight through the paragraph-mark chain, so without the reset the
  // text following a highlighted marker rendered highlighted too.
  range.font.name = config.font.name;
  range.font.size = config.font.sizeBody;
  range.font.color = config.font.color ?? "#000000";
  range.font.bold = false;
  range.font.italic = false;
  // Booleans are safe to assign (the Word-for-Web rejection issue is
  // specific to the string values "None"/"NoColor"); without this reset a
  // ~sub~/^sup^ run's vertAlign bleeds into every subsequent insertText
  // through the paragraph-mark chain, exactly like bold/highlight do.
  range.font.subscript = false;
  range.font.superscript = false;
  clearHighlight(range);

  switch (segment.type) {
    case "bold":
      range.font.bold = true;
      break;

    case "italic":
      range.font.italic = true;
      break;

    case "subscript":
      range.font.subscript = true;
      if (segment.bold) range.font.bold = true;
      break;

    case "superscript":
      range.font.superscript = true;
      if (segment.bold) range.font.bold = true;
      break;

    case "link":
      range.font.color = config.link.color;
      range.font.underline = "Single";
      break;

    case "not_found":
      range.font.bold = true;
      range.font.italic = true;
      range.font.size = config.font.sizeHighlighted;
      if (segment.notFoundCategory) {
        const color = config.highlightColors[segment.notFoundCategory];
        if (color) {
          range.font.highlightColor = color;
        }
      }
      break;

    case "missing_info":
    case "cannot_resolve":
    case "missing_table":
      // [MISSING INFO – …] gap marker — Word's built-in `Red` highlight +
      // white text so the gap is unmissable. [CANNOT RESOLVE: …] and
      // [MISSING TABLE: …] share the treatment: same "this is a gap"
      // semantics. Matches the backend's `w:highlight w:val="red"` exactly
      // so a live-streamed section and one rehydrated from the server .docx
      // (base64) render identically.
      range.font.italic = true;
      range.font.bold = true;
      range.font.size = config.font.sizeHighlighted;
      range.font.color = "#FFFFFF";
      range.font.highlightColor = "Red";
      break;

    case "reviewer":
      range.font.bold = true;
      range.font.italic = true;
      range.font.size = config.font.sizeHighlighted;
      range.font.highlightColor = config.reviewerHighlight;
      break;

    case "regulator_flag":
      // [REGULATOR FLAG: …] regulatory-audit recommendation — blue italic
      // text + Turquoise highlight, distinct from gap red and reviewer gray.
      applyRegulatorFlagStyle(range, config);
      break;

    case "language_flag":
      // [LANGUAGE: …] language-check recommendation (US-English spelling) —
      // green italic text + BrightGreen highlight, distinct from gap red,
      // regulator blue, and reviewer gray. Matches flags.yaml id "language".
      range.font.italic = true;
      range.font.size = config.font.sizeHighlighted;
      range.font.color = "#1B5E20";
      range.font.highlightColor = "BrightGreen";
      break;

    case "fill":
      range.font.italic = true;
      range.font.color = config.fillColor;
      break;

    default:
      // Base font already applied above
      break;
  }

  // ─── Trailing cap for styled (highlighted) segments ──────────────────
  // Append a zero-width-space (``U+200B``) with neutral formatting after
  // styled inline segments — ``not_found`` / ``missing_info`` /
  // ``cannot_resolve`` / ``reviewer`` — so subsequent inline text in the
  // same paragraph AND the next paragraph don't inherit the styled run's
  // highlight via the Office.js paragraph-mark chain.
  //
  // Without this cap, the paragraph mark inherits Pink / Yellow / etc.
  // from the last styled run; the next ``insertText("text", "End")`` in
  // the same paragraph picks it up, and the next paragraph created via
  // ``cursor.insertParagraph("", "After")`` inherits the mark too —
  // observable in the doc as a full-paragraph background highlight on
  // every paragraph following ``Prose: [MISSING INFO: …]``.
  //
  // Same mechanism, same character, same neutral font setters as the
  // ``renderHighlightedParagraph`` cap that handles standalone-marker
  // paragraphs. The ``if`` guard skips this work for plain ``text`` /
  // ``bold`` / ``italic`` / ``link`` / ``fill`` segments — they don't
  // touch ``font.highlightColor`` so there's nothing to neutralise.
  //
  // The cap's highlight is cleared via ``clearHighlight`` (``null``, the
  // documented removal value). The old assumption that a fresh
  // ``insertText`` range defaults to no-highlight does NOT hold on Word
  // for Web — new runs inherit the previous run's highlight — which is
  // how marker highlights bled into the rest of the section. We still
  // never assign ``font.underline`` (``"None"`` remains rejected).
  if (
    segment.type === "not_found" ||
    segment.type === "missing_info" ||
    segment.type === "cannot_resolve" ||
    segment.type === "missing_table" ||
    segment.type === "reviewer" ||
    segment.type === "regulator_flag" ||
    segment.type === "language_flag"
  ) {
    const cap = paragraph.insertText("​", "End");
    cap.font.name = config.font.name;
    cap.font.size = config.font.sizeBody;
    cap.font.color = config.font.color ?? "#000000";
    cap.font.bold = false;
    cap.font.italic = false;
    clearHighlight(cap);
  }
}

/**
 * Render a full-paragraph highlighted marker (entire line is [NOT FOUND] or [REVIEWER:]).
 */
function renderHighlightedParagraph(
  insertAfter: Word.Paragraph,
  segment: InlineSegment,
  config: FormatterConfig,
): Word.Paragraph {
  const para = insertAfter.insertParagraph("", "After");
  para.styleBuiltIn = "Normal";

  const range = para.insertText(segment.text, "End");
  range.font.name = config.font.name;
  range.font.size = config.font.sizeHighlighted;
  range.font.bold = true;
  range.font.italic = true;

  if (segment.type === "not_found" && segment.notFoundCategory) {
    const color = config.highlightColors[segment.notFoundCategory];
    if (color) {
      range.font.highlightColor = color;
    }
  } else if (
    segment.type === "missing_info" ||
    segment.type === "cannot_resolve" ||
    segment.type === "missing_table"
  ) {
    // Red highlight + white text for [MISSING INFO – …], [CANNOT RESOLVE: …],
    // and [MISSING TABLE: …] gap tags; matches the inline run styling above
    // and the backend's `w:highlight w:val="red"`.
    range.font.color = "#FFFFFF";
    range.font.highlightColor = "Red";
  } else if (segment.type === "reviewer") {
    range.font.highlightColor = config.reviewerHighlight;
  } else if (segment.type === "regulator_flag") {
    // Blue italic text + Turquoise highlight — matches the inline run styling.
    applyRegulatorFlagStyle(range, config);
  } else if (segment.type === "language_flag") {
    // Green italic text + BrightGreen highlight — matches the inline run styling.
    range.font.color = "#1B5E20";
    range.font.highlightColor = "BrightGreen";
  }

  // ─── Trailing neutral cap ────────────────────────────────────────────
  // Append a zero-width-space (``U+200B``) with neutral font properties
  // AFTER the styled run. Office.js stores the paragraph mark's font
  // properties as those of the LAST inserted run; without this cap, the
  // mark inherits the styled run's highlight (e.g. Pink for a
  // ``[MISSING INFO: …]`` marker) and the NEXT paragraph created via
  // ``insertParagraph("", "After")`` on this paragraph picks it up via
  // its own paragraph mark, bleeding the highlight into prose. With the
  // cap, the mark inherits the cap's neutral values and subsequent
  // paragraphs render un-highlighted.
  //
  // The cap's highlight is cleared via ``clearHighlight`` (``null``, the
  // documented removal value — unlike the string "NoColor" Word for Web
  // rejects). Fresh ``insertText`` ranges do NOT default to no-highlight
  // on Word for Web (they inherit the previous run's), so the explicit
  // clear is what actually stops the marker highlight from bleeding into
  // the next paragraph. ``font.underline`` stays untouched (``"None"``
  // remains rejected).
  //
  // The cap is one zero-width character — visually invisible,
  // copy-paste-safe, no impact on downstream consumers of the doc text.
  const cap = para.insertText("​", "End");
  cap.font.name = config.font.name;
  cap.font.size = config.font.sizeBody;
  cap.font.color = "#000000";
  cap.font.bold = false;
  cap.font.italic = false;
  clearHighlight(cap);

  return para;
}

// ---------------------------------------------------------------------------
// Image renderer — fetches image as base64, inserts as inline picture
// ---------------------------------------------------------------------------

/**
 * Pending image insertions. Because Office JS sync() is batched inside
 * formatAndInsert, we can't async-fetch images inline. Instead, we
 * insert a placeholder paragraph and queue the image URL for a second
 * pass. The caller (formatAndInsert in index.ts) runs the second pass
 * after the initial sync.
 */
// ---------------------------------------------------------------------------
// Structured table renderer (MR #55 — 2026-05-13)
// ---------------------------------------------------------------------------

/**
 * Convert a ``StructuredTableData`` descriptor to an HTML ``<table>`` string.
 *
 * Handles:
 * - ``grid_span`` → HTML ``colspan`` (horizontal cell merges)
 * - ``v_merge`` → HTML ``rowspan`` (vertical cell merges)
 * - ``shading_hex`` → ``style="background-color:#…"``
 * - ``bold`` / ``italic`` / ``font_size_pt`` / ``alignment`` → inline ``style``
 *
 * Word's ``Range.insertHtml`` converts these natively to proper merged Word
 * table cells — no ``mergeCells`` / ``cell.merge()`` API needed at all.
 *
 * WHY HTML instead of insertTable + mergeCells:
 * - Word.Table.mergeCells is WordApiDesktop 1.1 (desktop-only, not in
 *   standard WordApi, never declared in any manifest). It was cast to
 *   ``any`` and threw TypeError silently at runtime — all grid_span
 *   merges were no-ops, leaving every header cell 1 column wide.
 * - Using insertHtml lets Word's own HTML converter handle colspan /
 *   rowspan natively with zero dependency on merge APIs.
 */
function structuredTableToHtml(data: StructuredTableData): string {
  const rows = data.rows;

  // Pre-compute rowspan for v_merge="restart" cells.
  // rowspanMap : "r,physCol" → rowspan count
  // skipSet    : "r,physCol" → omit <td> (space is owned by restart cell above)
  const rowspanMap = new Map<string, number>();
  const skipSet = new Set<string>();

  for (let r = 0; r < rows.length; r++) {
    const cells = rows[r].cells ?? [];
    let physCol = 0;

    for (const cell of cells) {
      const span = cell.grid_span && cell.grid_span > 0 ? cell.grid_span : 1;

      if (cell.v_merge === "restart") {
        let rowspan = 1;
        for (let rr = r + 1; rr < rows.length; rr++) {
          const nextCells = rows[rr].cells ?? [];
          let pc = 0;
          let isContinue = false;
          for (const nc of nextCells) {
            const ns = nc.grid_span && nc.grid_span > 0 ? nc.grid_span : 1;
            if (pc === physCol && nc.v_merge === "continue") {
              isContinue = true;
              for (let s = 0; s < ns; s++) skipSet.add(`${rr},${pc + s}`);
              break;
            }
            pc += ns;
            if (pc > physCol) break;
          }
          if (isContinue) rowspan++;
          else break;
        }
        if (rowspan > 1) rowspanMap.set(`${r},${physCol}`, rowspan);
      }

      physCol += span;
    }
  }

  const tableRows = rows
    .map((row, r) => {
      let physCol = 0;
      const tds = (row.cells ?? [])
        .map((cell) => {
          const span = cell.grid_span && cell.grid_span > 0 ? cell.grid_span : 1;
          const colStart = physCol;
          physCol += span;

          // Skip continuation cells — the restart cell's rowspan covers them.
          if (cell.v_merge === "continue" || skipSet.has(`${r},${colStart}`)) {
            return "";
          }

          const attrs: string[] = [];
          const styles: string[] = ["border:1px solid #000000", "padding:2px 4px"];

          if (span > 1) attrs.push(`colspan="${span}"`);
          const rs = rowspanMap.get(`${r},${colStart}`);
          if (rs && rs > 1) attrs.push(`rowspan="${rs}"`);

          if (cell.shading_hex) styles.push(`background-color:#${cell.shading_hex}`);
          if (cell.bold) styles.push("font-weight:bold");
          if (cell.italic) styles.push("font-style:italic");
          if (cell.font_size_pt) styles.push(`font-size:${cell.font_size_pt}pt`);
          if (cell.alignment && cell.alignment !== "left") {
            styles.push(`text-align:${cell.alignment}`);
          }
          attrs.push(`style="${styles.join(";")}"`);

          const text = (cell.text ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

          return `<td ${attrs.join(" ")}>${text}</td>`;
        })
        .join("");
      return `<tr>${tds}</tr>`;
    })
    .join("");

  return `<table style="border-collapse:collapse;border:1px solid #000000;width:100%">${tableRows}</table>`;
}

/**
 * Render a structured table node.
 *
 * Uses insertHtml (via a lead anchor paragraph) so that Word's native HTML
 * converter handles colspan/rowspan — no mergeCells API required.
 *
 * POSITION: trailAnchor is created before the HTML is inserted, so it is
 * unconditionally body-scoped. Returning it guarantees the next node
 * renders outside the table.
 */
function renderStructuredTable(
  insertAfter: Word.Paragraph,
  node: ParsedNode,
  _config: FormatterConfig,
): Word.Paragraph {
  const data = node.structuredTable;
  if (!data?.rows?.length) return insertAfter;

  const html = structuredTableToHtml(data);
  const leadAnchor = insertAfter.insertParagraph("", "After");
  const trailAnchor = leadAnchor.insertParagraph("", "After");
  leadAnchor.getRange("Whole").insertHtml(html, "Replace");
  return trailAnchor;
}

// ---------------------------------------------------------------------------

export const pendingImages: Array<{
  paragraph: Word.Paragraph;
  url: string;
  alt: string;
  /** True when the placeholder lives inside a table cell. The image second
   *  pass then constrains the picture to a cell-appropriate width and skips
   *  the standalone caption paragraph (the row already labels the figure). */
  inCell?: boolean;
}> = [];

function renderImage(insertAfter: Word.Paragraph, node: ParsedNode, config: FormatterConfig): Word.Paragraph {
  const url = node.imageUrl || "";
  const alt = node.imageAlt || "";

  // Insert a placeholder paragraph that will be replaced with the
  // actual image in a second pass (after context.sync). We can't
  // do the async fetch inside this synchronous renderer.
  const para = insertAfter.insertParagraph(`[Image: ${alt}]`, "After");
  para.font.name = config.font.name;
  para.font.size = config.font.sizeBody;
  para.font.italic = true;
  para.font.color = config.image.captionColor;

  // Queue for second-pass insertion
  if (url) {
    pendingImages.push({ paragraph: para, url, alt });
  }

  return para;
}

/**
 * Second-pass: replace image placeholders with actual inline pictures.
 *
 * Called by formatAndInsert AFTER the first context.sync(). Fetches
 * each queued image URL as base64 and inserts it via
 * insertInlinePictureFromBase64.
 *
 * This two-pass approach is needed because:
 *   1. The first pass (renderNodes) inserts paragraphs into the
 *      caller's Word.run batch, syncing between every node so the
 *      proxy chain stays resolved.
 *   2. Image fetching is async (network) and must happen outside
 *      the caller's text-insertion Word.run.
 *   3. The second pass runs in its own Word.run context per image
 *      so the paragraph references are still valid AND a single
 *      slow / failed image doesn't block the others.
 */
export async function insertPendingImages(
  context: Word.RequestContext,
  config?: import("./formatterConfig").FormatterConfig,
): Promise<number> {
  if (pendingImages.length === 0) return 0;

  const imgConfig = config?.image ?? {
    fetchTimeout: 5000,
    failurePlaceholder: "[Image could not be loaded]",
    captionColor: "#808080",
  };
  const fontName = config?.font?.name ?? "Times New Roman";

  const apiBase = (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE || "/api";
  let inserted = 0;

  for (const { paragraph, url, alt } of pendingImages) {
    try {
      const fullUrl = url.startsWith("/api/")
        ? url.replace("/api/", `${apiBase}/`)
        : url.startsWith("http")
          ? url
          : `${apiBase}/${url}`;

      // Per-image timeout — one slow/failed image doesn't block the rest
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), imgConfig.fetchTimeout);
      // credentials: "include" ensures the JWT cookie is sent even from
      // Word's cross-origin iframe context. Images are also exempt from
      // the JWT middleware (see server/main.py jwt_guard_middleware), so
      // this is belt-and-suspenders.
      const resp = await fetch(fullUrl, {
        signal: controller.signal,
        credentials: "include",
      });
      clearTimeout(timer);

      if (!resp.ok) {
        console.warn(`[formatter] Image fetch failed: ${fullUrl} (${resp.status})`);
        // Show failure placeholder instead of the loading placeholder
        paragraph.clear();
        const failRange = paragraph.insertText(imgConfig.failurePlaceholder, "Start");
        failRange.font.name = fontName;
        failRange.font.size = 9;
        failRange.font.italic = true;
        failRange.font.color = imgConfig.captionColor;
        continue;
      }

      const blob = await resp.blob();
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          resolve(result.split(",")[1] || "");
        };
        reader.readAsDataURL(blob);
      });

      if (!base64) continue;

      paragraph.clear();
      const picture = paragraph.insertInlinePictureFromBase64(base64, "Start");
      picture.width = 396;
      picture.lockAspectRatio = true;

      if (alt) {
        const caption = paragraph.insertParagraph(alt, "After");
        caption.font.name = fontName;
        caption.font.size = 9;
        caption.font.italic = true;
        caption.font.color = imgConfig.captionColor;
        caption.alignment = "Centered";
      }

      await context.sync();
      inserted++;
    } catch (err) {
      // Graceful degradation: log the failure and show placeholder,
      // but continue inserting the remaining images.
      console.warn(`[formatter] Image insert failed for ${url}:`, err);
      try {
        paragraph.clear();
        const failRange = paragraph.insertText(imgConfig.failurePlaceholder, "Start");
        failRange.font.name = fontName;
        failRange.font.size = 9;
        failRange.font.italic = true;
        failRange.font.color = imgConfig.captionColor;
      } catch {
        /* paragraph ref may be invalid */
      }
    }
  }

  // Clear the queue
  pendingImages.length = 0;
  return inserted;
}
